import NCTable from "../../shared/table/NCTable"
import { ACTIVITY_STATUS, SEARCH_CONFIG, TABLE_CONFIG } from "../../libs/config"
import { useState } from "react"
import { Grid } from "@material-ui/core"
import { Form, FormikProvider, useFormik } from "formik"
import * as Yup from "yup"
import FormSection from "../FormSection"
import NCTableStatus from "../../shared/table/NCTableStatus"
import useDebounce from "../../libs/hooks/Debounce"
import { useGetGuardianList } from "../../services/queries"
import { useUpdateClient } from "../../services/mutations"
import { useQueryClient } from "react-query"
import NCButton from "../../shared/NCButton"
import NCAutocomplete from "../../shared/NCAutocomplete"
import NCModal from "../../shared/NCModal"
import FormField from "../FormField"
import NCLoader from "../../shared/NCLoader"
import { AddIcon } from "../../libs/icons"
import NCSvgIcon from "../../shared/NCSvgIcon"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"

const GuardianList = ({ clientId }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [query] = useState("")
  const [pageSize, setPageSize] = useState(TABLE_CONFIG.PAGE_SIZE)
  const [ordering, setOrdering] = useState("id")
  const [modalOpen, setModalOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(query, SEARCH_CONFIG.TIME_IN_MILLI)
  const { isLoading, data, refetch } = useGetGuardianList(
    page, debouncedSearchTerm, pageSize,
    { ordering, client: clientId }
  )
  const { data: allGuardiansData } = useGetGuardianList(1, "", 1000, {})
  const allGuardianOptions = (allGuardiansData?.data?.data?.results || []).map(g => ({
    id: g.id,
    name: g.name || `${g.first_name} ${g.last_name}`
  }))
  const updateClient = useUpdateClient()
  const { next, previous, total_pages, results } = data?.data?.data || {}
  const existingGuardianIds = (results || []).map(g => g.id)

  const formik = useFormik({
    initialValues: { guardian: "" },
    validationSchema: Yup.object().shape({
      guardian: Yup.string().required(t("client.error.guardian_required"))
    }),
    onSubmit(values) {
      const allIds = [...existingGuardianIds, values.guardian]
      updateClient.mutate(
        { id: clientId, guardian: allIds },
        {
          onSuccess: () => {
            toast.success(t("client.guardian_add"))
            queryClient.invalidateQueries(["getGuardianList"])
            refetch()
            setModalOpen(false)
            formik.resetForm()
          }
        }
      )
    }
  })

  let columns = [
    { Header: t("list.guardian_name"), accessor: "name", disableSortBy: true },
    { Header: t("list.guardian_email"), accessor: "email", disableSortBy: true },
    { Header: t("common.phone_number"), accessor: "phone", disableSortBy: true },
    { Header: t("common.status"), id: "status",
      accessor: cell => <NCTableStatus status={ACTIVITY_STATUS[cell.status]} />,
      disableSortBy: true }
  ]

  const guardianModal = (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormField
              as={NCAutocomplete}
              options={(allGuardianOptions || []).filter(
                g => !existingGuardianIds.includes(g.id)
              )}
              label={t("client.select_guardian")}
              name={"guardian"}
              onChange={v => formik.setFieldValue("guardian", v)}
              required={true}
              error={formik?.errors?.guardian}
            />
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )

  return (
    <FormSection
      title={t("client.guardian_details")}
      subTitle={
        <NCButton
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
          endIcon={<NCSvgIcon component={AddIcon} inheritViewBox />}
        >
          {t("client.add_guardian")}
        </NCButton>
      }
    >
      <NCTable
        columns={columns}
        data={results || []}
        page={page}
        onNext={() => setPage(p => next ? p + 1 : p)}
        onPrev={() => setPage(p => previous ? p - 1 : p)}
        pageCount={total_pages}
        gotoPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        sorting={ordering}
        setSorting={setOrdering}
        isLoading={isLoading}
        inlineFooter={true}
      />
      <NCModal
        open={modalOpen}
        title={t("client.add_guardian")}
        text={guardianModal}
        onClose={() => { setModalOpen(false); formik.resetForm() }}
        submitButton={
          <NCButton
            variant="contained"
            color="primary"
            disableElevation
            onClick={formik.handleSubmit}
            style={{ width: "auto" }}
          >
            {t("common.save")}
          </NCButton>
        }
      />
      {updateClient.isLoading && <NCLoader open={true} />}
    </FormSection>
  )
}

export default GuardianList
